import { create } from 'zustand';

import {
  Dialog,
  DialogMessage,
  Message,
  MessageUI
} from '@/api/messaging/types.ts';
import MessagingController from '@/api/messaging/messaging.ts';
import RelationController from '@/api/relation/relation.ts';
import {
  MessageLabels,
  MessageTypes,
  ReportReason,
  UserStates
} from '@/enums/apiEnums.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';

interface RemoveDialogParams {
  hid: string;
  shouldCallApi?: boolean;
}

export const FETCH_LIMIT = 50;

interface DialogsStore {
  dialogs: { data: Dialog[] };
  fetchLimit: number;
  hasUnreadEvents: boolean;
  isFetching: boolean;
  nextPageToken: string | undefined;
  allDialogsLoaded: boolean;
  scrollPosition: number; // to save and restore position in messenger on tab change
  appendToTopDialogWS: (lastMessage?: DialogMessage) => void;
  fetchDialogs: () => Promise<void>;
  markDialogAsRead: (hid: string) => void;
  updateDialogWithOutcomeMessage: (
    hid: string,
    outcomeMessage: MessageUI
  ) => void;
  updateDialogContactOnlineState: (
    hid: string,
    onlineState: UserStates
  ) => void;
  removeDialog: (params: RemoveDialogParams) => Promise<void>;
  blockUserAndRemoveDialog: (
    hid: string,
    reason: ReportReason
  ) => Promise<void>;
  acceptGift: (hid: string) => Promise<void>;
  declineGift: (hid: string) => Promise<void>;
  removeAllDialogs: () => void;
  setHasUnreadEvents: (value: boolean) => void;
  setScrollPosition: (position: number) => void;
  resetScrollPosition: () => void;
}

export const useDialogsStore = create<DialogsStore>((set, get) => ({
  dialogs: { data: [] },
  hasUnreadEvents: false,
  fetchLimit: FETCH_LIMIT,
  isFetching: false,
  nextPageToken: undefined,
  scrollPosition: 0,
  allDialogsLoaded: false,
  appendToTopDialogWS: (lastMessage?: DialogMessage) => {
    if (lastMessage && lastMessage.message) {
      const socketContactId = lastMessage.contact.hid;
      const socketLastMessage = lastMessage.message;
      const socketLastMessageMicros = lastMessage.message.create_micros;
      const socketContactName = lastMessage.contact.name;
      const socketContactPhoto = lastMessage.contact.photo_url;
      const socketContactAge = lastMessage.contact.age;

      set((state) => {
        const existingDialogIndex = state.dialogs.data.findIndex(
          (dialog) => dialog.contact.hid === socketContactId
        );

        if (existingDialogIndex !== -1) {
          const updatedDialogs = [...state.dialogs.data];
          updatedDialogs[existingDialogIndex] = {
            ...updatedDialogs[existingDialogIndex],
            last_message: socketLastMessage,
            last_message_micros: socketLastMessageMicros,
            contact: {
              ...updatedDialogs[existingDialogIndex].contact,
              online_state: UserStates.ONLINE_STATE_ONLINE
            }
          };

          const [updatedDialog] = updatedDialogs.splice(existingDialogIndex, 1);
          return { dialogs: { data: [updatedDialog, ...updatedDialogs] } };
        }

        const newDialog: Dialog = {
          contact: {
            hid: socketContactId,
            name: socketContactName,
            age: socketContactAge,
            photo_url: socketContactPhoto,
            online_state: lastMessage.contact.online_state,
            gender: lastMessage.contact.gender
          },
          labels: [],
          last_message: socketLastMessage,
          last_message_micros: socketLastMessageMicros,
          last_inbox_read_message_micros: '0'
        };

        return { dialogs: { data: [newDialog, ...state.dialogs.data] } };
      });
    }
  },
  fetchDialogs: async () => {
    const { isFetching, allDialogsLoaded, fetchLimit, nextPageToken, dialogs } =
      get();

    if (isFetching || allDialogsLoaded) return;

    set({ isFetching: true });

    try {
      const fetchedDialogs = await MessagingController.getDialogs({
        limit: fetchLimit,
        nextPageToken: nextPageToken
      });

      const fetchedDialogsArray = Array.isArray(fetchedDialogs.dialogs)
        ? fetchedDialogs.dialogs
        : [];

      if (fetchedDialogsArray.length < fetchLimit) {
        set({ allDialogsLoaded: true });
      }

      set({
        dialogs: {
          data: [
            ...new Map(
              [...dialogs.data, ...fetchedDialogsArray].map((dialog) => [
                dialog.contact.hid,
                dialog
              ])
            ).values()
          ]
        },
        nextPageToken: fetchedDialogs.next_page_token
      });
    } catch (e) {
      catchErrors(e, true);
    } finally {
      set({ isFetching: false });
    }
  },
  markDialogAsRead: (hid: string) => {
    set((state) => ({
      dialogs: {
        data: state.dialogs.data.map((dialog) =>
          dialog.contact.hid === hid
            ? {
                ...dialog,
                last_inbox_read_message_micros: dialog.last_message_micros
              }
            : dialog
        )
      }
    }));
  },
  updateDialogWithOutcomeMessage: (hid: string, outcomeMessage: Message) => {
    set((state) => {
      const dialogs = [...state.dialogs.data];
      const dialogIndex = dialogs.findIndex(
        (dialog) => dialog.contact.hid === hid
      );

      if (dialogIndex === -1) return { dialogs: { data: dialogs } }; // TODO if not found in state, try to take it from api, and append here

      const dialogToUpdate = dialogs[dialogIndex];

      const updatedLabels = dialogToUpdate.last_message.labels.filter(
        (label: string) => label !== MessageLabels.LABEL_INBOX.toString()
      );

      const updatedDialog = {
        ...dialogToUpdate,
        last_message: {
          ...dialogToUpdate.last_message,
          text_body: {
            ...dialogToUpdate.last_message.text_body,
            message: outcomeMessage.text_body?.message ?? ''
          },
          labels: updatedLabels,
          type: MessageTypes.TYPE_TEXT
        }
      };

      dialogs.splice(dialogIndex, 1); // Unset dialog form current position
      dialogs.unshift(updatedDialog); // Set on top position

      return { dialogs: { data: dialogs } };
    });
  },
  updateDialogContactOnlineState: (hid: string, onlineState: UserStates) => {
    set((state) => {
      const dialogs = [...state.dialogs.data];
      const dialogIndex = dialogs.findIndex(
        (dialog) => dialog.contact.hid === hid
      );

      if (dialogIndex === -1) {
        return { dialogs: { data: dialogs } };
      }

      const dialogToUpdate = dialogs[dialogIndex];

      dialogs[dialogIndex] = {
        ...dialogToUpdate,
        contact: {
          ...dialogToUpdate.contact,
          online_state: onlineState
        }
      };

      return { dialogs: { data: dialogs } };
    });
  },
  removeDialog: async ({ hid, shouldCallApi }) => {
    try {
      if (shouldCallApi) {
        await MessagingController.deleteDialog(hid);
      }

      set((state) => ({
        dialogs: {
          data: state.dialogs.data.filter(
            (dialog) => dialog.contact.hid !== hid
          )
        }
      }));
    } catch (e) {
      catchErrors(e, true);
    }
  },
  blockUserAndRemoveDialog: async (hid: string, reason: ReportReason) => {
    try {
      await RelationController.blockUser(hid, reason);

      set((state) => ({
        dialogs: {
          data: state.dialogs.data.filter(
            (dialog) => dialog.contact.hid !== hid
          )
        }
      }));
    } catch (e) {
      catchErrors(e, true);
    }
  },
  acceptGift: async (hid: string) => {
    try {
      await MessagingController.acceptGift(hid);
    } catch (e) {
      catchErrors(e, true);
    } finally {
      set((state) => {
        const existingDialogIndex = state.dialogs.data.findIndex(
          (dialog) => dialog.contact.hid === hid
        );

        if (existingDialogIndex === -1) return state;

        const dialogWithGift = state.dialogs.data[existingDialogIndex];

        const updatedDialog: Dialog = {
          ...dialogWithGift,
          last_message: {
            ...dialogWithGift.last_message,
            type: MessageTypes.TYPE_STICKER,
            labels: []
          }
        };

        const updatedDialogs = [...state.dialogs.data];
        updatedDialogs.splice(existingDialogIndex, 1);

        return { dialogs: { data: [updatedDialog, ...updatedDialogs] } };
      });
    }
  },
  declineGift: async (hid: string) => {
    try {
      await MessagingController.declineGift(hid);
    } catch (e) {
      catchErrors(e, true);
    } finally {
      set((state) => ({
        dialogs: {
          data: state.dialogs.data.filter(
            (dialog) => dialog.contact.hid !== hid
          )
        }
      }));
    }
  },
  removeAllDialogs: () =>
    set({
      dialogs: { data: [] },
      allDialogsLoaded: false,
      nextPageToken: undefined
    }),
  setHasUnreadEvents: (value) => set({ hasUnreadEvents: value }),
  setScrollPosition: (position: number) => set({ scrollPosition: position }),
  resetScrollPosition: () => set({ scrollPosition: 0 })
}));
